import { dataViaApi } from '../api/mode';
import { getListItems } from '../graph/lists';
import { LISTS } from './listNames';
import { uploadToAttachmentsLibrary } from '../graph/library';
import { compressPhoto } from '../local/imageCompression';
import { MAX_PDF_BYTES, type AttachmentKind } from '../audit/attachmentRules';

/**
 * Task file uploads reuse the AuditAttachments library (its TaskId column
 * was provisioned for exactly this) under a Task-{id} folder per task.
 * Uploads go online; with no signal the B4 task outbox keeps the prepared
 * file on the device until it can send. Photos go through the same JPEG
 * ≤500KB re-encode as audits, PDFs the same ≤10MB cap (spec §8).
 */

export interface TaskAttachmentRow {
  id: number;
  kind: AttachmentKind;
  filename: string;
  url: string;
  uploadedBy: string;
  /** When it was stored (the row's Created) — "proof required" counts files since ProofSince (B1). */
  uploadedAt: string | null;
}

interface AttachmentFields {
  FileLeafRef: string;
  /** The uploader's own file name, display only (since 2026-10-21; older rows: blank). */
  Title?: string;
  AttachmentType: string;
  UploadedBy: string;
}

/** Uploaded files for one task (TaskId indexed), oldest first. */
export async function getAttachmentsForTask(
  taskId: number,
): Promise<TaskAttachmentRow[]> {
  const items = await getListItems<AttachmentFields>(LISTS.attachmentsLibrary, {
    filter: `fields/TaskId eq ${taskId}`,
  });
  return items
    .filter((i) => i.webUrl) // skip the folder row itself if it matches
    .map((i) => ({
      id: Number(i.id),
      kind: (i.fields.AttachmentType === 'pdf' ? 'pdf' : 'photo') as AttachmentKind,
      filename: i.fields.Title?.trim() || i.fields.FileLeafRef || 'attachment',
      url: i.webUrl!,
      uploadedBy: i.fields.UploadedBy ?? '',
      uploadedAt: i.createdDateTime || null,
    }))
    .sort((a, b) => a.id - b.id);
}

/** Keep the original name recognizable but safe as a SharePoint filename. */
function safeName(name: string): string {
  return name.replace(/[\\/:*?"<>|#%&{}~]+/g, '_').slice(-80);
}

/**
 * The name a task file is SHOWN by — the uploader's own file name (store
 * round 2026-10-21: "task-61-1792…jpg" meant nothing to the store). Same rule
 * as the server's displayTitle (api/src/files.ts), which sets it in server
 * mode: last path part, no control/bidi characters, the real extension
 * (photos are re-encoded to JPEG), ≤ 120 characters. Display only — the
 * stored name stays app/server-made.
 */
export function taskFileTitle(name: string, kind: AttachmentKind): string {
  const ext = kind === 'photo' ? 'jpg' : 'pdf';
  const base = (name.split(/[\\/]/).pop() ?? '')
    .replace(/\s+/g, ' ')
    // Every control / format / unassigned code point (bidi marks, BOM, soft hyphen, tags…).
    .replace(/\p{C}/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
    // Only a real file extension goes ("Report v1.2.pdf" keeps its "v1.2").
    .replace(/\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?|pdf)$/i, '')
    .trim();
  // Cut by code points, never half a surrogate pair.
  const capped = [...base].slice(0, 115).join('').trim();
  return capped ? `${capped}.${ext}` : '';
}

/** A file made ready to send: photos re-encoded to JPEG, PDFs checked. */
export interface PreparedTaskFile {
  kind: AttachmentKind;
  blob: Blob;
  /** The person's own file name, as the stored file's display Title ('' = none). */
  title: string;
  /** The name the upload carries (server mode: the original; direct mode: built at send). */
  sourceName: string;
}

/**
 * Compress (photos) or validate (PDFs). Throws with a user-showable message
 * on an unsupported type or an oversized PDF. Works without signal — the
 * offline queue (B4) keeps the result on the device.
 */
export async function prepareTaskFile(file: File): Promise<PreparedTaskFile> {
  const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
  if (isPdf) {
    if (file.size > MAX_PDF_BYTES) throw new Error('PDFs must be 10 MB or smaller.');
    return { kind: 'pdf', blob: file, title: taskFileTitle(file.name, 'pdf'), sourceName: file.name };
  }
  if (file.type.startsWith('image/')) {
    // Always lands as JPEG.
    return { kind: 'photo', blob: await compressPhoto(file), title: taskFileTitle(file.name, 'photo'), sourceName: file.name };
  }
  throw new Error('Only photos and PDF files can be attached.');
}

/**
 * Upload one prepared file. Returns the name to show for it (for the task
 * timeline's 'file' event row): the uploader's own name, else the stored
 * name. `uploadKey` (server mode): a resend with the same key returns the
 * same file instead of a second copy (the offline queue's retries).
 */
export async function uploadPreparedTaskFile(
  task: { id: number; storeId: number },
  prepared: PreparedTaskFile,
  uploadedBy: string,
  uploadKey?: string,
): Promise<string> {
  const stamp = Date.now();
  const { kind, blob, title } = prepared;
  // Server mode names task files itself (task-{id}-…) and keeps only the
  // original name as the display Title; direct mode builds the full name here.
  const filename =
    kind === 'pdf'
      ? dataViaApi()
        ? prepared.sourceName
        : `task-${task.id}-${stamp}-${safeName(prepared.sourceName)}`
      : dataViaApi()
        ? title || 'photo.jpg'
        : `task-${task.id}-${stamp}.jpg`;
  const uploaded = await uploadToAttachmentsLibrary(
    `Task-${task.id}`,
    filename,
    blob,
    {
      TaskId: task.id,
      StoreId: task.storeId,
      AttachmentType: kind,
      UploadedBy: uploadedBy,
      ...(title ? { Title: title } : {}),
    },
    uploadKey,
  );
  return title || uploaded.filename;
}

